import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, CalendarClock, CheckCircle2, MonitorSmartphone } from 'lucide-react';
import type { User, Vacancy } from '../types';
import type { ProposalRow, InterviewRow, OfferRow } from '../data/clientEngagementMock';
import {
  supabase_get_portal_engagement_applications,
  supabase_get_portal_engagement_interviews,
  supabase_get_placements_for_vacancy,
  extractErrorMessage,
  supabase,
} from '../services/supabaseService';
import {
  mapApplicationToProposalRow,
  mapInterviewRow,
  mapPlacementToOfferRow,
  proposalNeedsClientAttention,
  isPipelineVoorgesteld,
} from '../utils/portalEngagementMappers';
import { includeInVacancyVoorgesteldClientList } from '../utils/vacancyClientPortalStage';
import { normalizeCvSnapshotForPortal } from '../utils/portalProposalThread';
import CandidateProposalCard from './clientEngagementCenter/CandidateProposalCard';
import VacancyClientPortalCandidateEditor from './VacancyClientPortalCandidateEditor';
import EngagementCandidateModal from './clientEngagementCenter/EngagementCandidateModal';
import LoadingState from './clientEngagementCenter/LoadingState';
import EmptyState from './clientEngagementCenter/EmptyState';
import { formatNlDateTime, interviewStatusLabel, interviewTypeLabel, offerStatusLabel, formatNlDate } from './clientEngagementCenter/engagementLabels';

interface VacancyClientPortalTabProps {
  vacancy: Vacancy;
  user: User;
}

function portalOrgClientId(v: Vacancy): string {
  const anyV = v as Record<string, unknown>;
  const ek = anyV.eindklant as { id?: string } | undefined;
  const op = anyV.opdrachtgever as { id?: string } | undefined;
  return String(anyV.eindklant_id || ek?.id || anyV.opdrachtgever_id || op?.id || v.clientId || '').trim();
}

function snapshotForApplication(app: any): Record<string, unknown> | null {
  const snap = normalizeCvSnapshotForPortal(app?.cv_snapshot);
  return Object.keys(snap).length ? snap : null;
}

function hasPublishedPortalSignals(app: any): boolean {
  const snap = snapshotForApplication(app);
  if (!snap) return false;
  if (snap.portal_client_proposal && typeof snap.portal_client_proposal === 'object') return true;
  if (snap.portal_client_cv_formatted && typeof snap.portal_client_cv_formatted === 'object') return true;
  if (Array.isArray(snap.portal_thread) && snap.portal_thread.length > 0) return true;
  return false;
}

const noop = () => {};

const VacancyClientPortalTab: React.FC<VacancyClientPortalTabProps> = ({ vacancy, user }) => {
  const vacancyId = String(vacancy.id || '').trim();
  const clientIdForMap = portalOrgClientId(vacancy) || String(user.clientId || '').trim();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rawApps, setRawApps] = useState<any[]>([]);
  const [snapshotsByApplicationId, setSnapshotsByApplicationId] = useState<Record<string, Record<string, unknown> | null>>({});
  const [interviews, setInterviews] = useState<InterviewRow[]>([]);
  const [offers, setOffers] = useState<OfferRow[]>([]);

  const [candidateModal, setCandidateModal] = useState<{ open: boolean; proposal: ProposalRow | null }>({
    open: false,
    proposal: null,
  });

  const reloadDebounceRef = useRef<number | null>(null);

  const load = useCallback(async () => {
    if (!vacancyId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const [appsRaw, intsRaw] = await Promise.all([
        supabase_get_portal_engagement_applications([vacancyId]),
        supabase_get_portal_engagement_interviews([vacancyId]),
      ]);

      const snapMap: Record<string, Record<string, unknown> | null> = {};
      for (const a of appsRaw || []) {
        const id = String((a as any)?.id || '');
        if (!id) continue;
        snapMap[id] = snapshotForApplication(a);
      }
      setSnapshotsByApplicationId(snapMap);
      setRawApps(appsRaw || []);

      const interviewsMapped = (intsRaw || []).map((r) => mapInterviewRow(r, clientIdForMap));
      setInterviews(interviewsMapped);

      void supabase_get_placements_for_vacancy(vacancyId)
        .then((placementsRaw) => {
          const rows = (placementsRaw || []).map((p) => mapPlacementToOfferRow(p, clientIdForMap));
          setOffers(rows);
        })
        .catch(() => setOffers([]));
    } catch (e) {
      console.error('[VacancyClientPortalTab]', e);
      setLoadError(extractErrorMessage(e) || 'Kon gegevens niet laden.');
    } finally {
      setLoading(false);
    }
  }, [vacancyId, clientIdForMap]);

  useEffect(() => {
    void load();
  }, [load]);

  const scheduleReload = useCallback(() => {
    if (reloadDebounceRef.current != null) window.clearTimeout(reloadDebounceRef.current);
    reloadDebounceRef.current = window.setTimeout(() => {
      reloadDebounceRef.current = null;
      void load();
    }, 450);
  }, [load]);

  useEffect(() => {
    return () => {
      if (reloadDebounceRef.current != null) window.clearTimeout(reloadDebounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (!vacancyId) return;
    const channel = supabase
      .channel(`vacancy-client-portal-apps-${vacancyId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'applications',
          filter: `vacancy_id=eq.${vacancyId}`,
        },
        () => scheduleReload(),
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [vacancyId, scheduleReload]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [load]);

  const publishedPortalCount = useMemo(() => {
    let c = 0;
    for (const a of rawApps || []) {
      if (hasPublishedPortalSignals(a)) c += 1;
    }
    return c;
  }, [rawApps]);

  const voorgesteldPairs = useMemo(() => {
    const list = (rawApps || []).filter(includeInVacancyVoorgesteldClientList);
    return list.map((app) => ({
      app,
      proposal: mapApplicationToProposalRow(app, clientIdForMap),
    }));
  }, [rawApps, clientIdForMap]);

  const voorgesteldActivePairs = useMemo(
    () => voorgesteldPairs.filter(({ proposal }) => proposal.status !== 'rejected'),
    [voorgesteldPairs],
  );

  const voorgesteldRejectedPairs = useMemo(
    () => voorgesteldPairs.filter(({ proposal }) => proposal.status === 'rejected'),
    [voorgesteldPairs],
  );

  const overigeActieList = useMemo(() => {
    const out: ProposalRow[] = [];
    for (const app of rawApps || []) {
      if (includeInVacancyVoorgesteldClientList(app)) continue;
      const p = mapApplicationToProposalRow(app, clientIdForMap);
      if (proposalNeedsClientAttention(p) && !isPipelineVoorgesteld(p.pipelineStatus) && p.status !== 'waiting_feedback') {
        out.push(p);
      }
    }
    return out;
  }, [rawApps, clientIdForMap]);

  const openCandidate = (p: ProposalRow) => setCandidateModal({ open: true, proposal: p });
  const closeCandidate = () => setCandidateModal({ open: false, proposal: null });

  const modalSnapshot =
    candidateModal.proposal != null ? snapshotsByApplicationId[candidateModal.proposal.proposalId] ?? null : null;

  if (loading && rawApps.length === 0) {
    return (
      <div className="flex h-full min-h-[240px] items-center justify-center bg-[#f8fafc] p-6">
        <LoadingState message="Clientportaal laden…" />
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-[#f8fafc] p-4 lg:p-6 animate-fadeIn custom-scrollbar">
      <div className="mx-auto max-w-4xl space-y-6 pb-10">
        <div className="rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50/90 to-white p-4 shadow-sm">
          <div className="flex flex-wrap items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-600 text-white shadow-md">
              <MonitorSmartphone className="h-5 w-5" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-bold text-slate-900">Clientportaal (recruiterweergave)</h2>
              <p className="mt-1 text-sm text-slate-600">
                Zelfde dossiers als in het klantportaal voor deze vacature. Onder «Voorgestelde kandidaten» kun je als
                recruiter samenvatting, matchtekst, beschikbaarheid en salaris bijwerken, een AI-match laten lopen en
                berichten naar de klant sturen. De klant voert uitnodigingen en portaal-acties uit in het echte portaal.
              </p>
              {loadError ? (
                <p className="mt-2 text-sm font-semibold text-red-700">{loadError}</p>
              ) : (
                <p className="mt-2 text-xs text-slate-500">
                  {publishedPortalCount} sollicitatie{publishedPortalCount === 1 ? '' : 's'} met publicatie of thread / document
                  op deze vacature.
                </p>
              )}
            </div>
          </div>
        </div>

        <section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-700">
              <AlertCircle className="h-5 w-5" />
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums text-slate-900">
                {voorgesteldActivePairs.length + overigeActieList.length}
              </p>
              <p className="text-xs font-medium text-slate-500">Openstaande dossiers</p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
              <CalendarClock className="h-5 w-5" />
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums text-slate-900">{interviews.length}</p>
              <p className="text-xs font-medium text-slate-500">Gesprekken (venster)</p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums text-slate-900">{offers.length}</p>
              <p className="text-xs font-medium text-slate-500">Plaatsingen (vacature)</p>
            </div>
          </div>
        </section>

        <section>
          <div className="mb-4 flex items-end justify-between gap-2 border-b border-slate-200 pb-3">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Voorgestelde kandidaten</h3>
              <p className="mt-1 text-sm text-slate-500">
                Alleen dossiers met echte voorgesteld-/portaalactiviteit (niet elke «New» op shortlist). Bewerk hier
                teksten en stuur updates naar de klant; pipeline-label toont de database-status.
              </p>
            </div>
            <span className="shrink-0 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
              {voorgesteldActivePairs.length}
            </span>
          </div>
          <div className="space-y-5">
            {voorgesteldActivePairs.length ? (
              voorgesteldActivePairs.map(({ app, proposal }) => (
                <VacancyClientPortalCandidateEditor
                  key={proposal.proposalId}
                  vacancy={vacancy}
                  application={app}
                  proposal={proposal}
                  user={user}
                  cvSnapshot={snapshotsByApplicationId[proposal.proposalId] ?? null}
                  onSaved={() => void load()}
                />
              ))
            ) : (
              <EmptyState
                title="Geen voorgestelde kandidaten"
                description="Zet kandidaten op «Voorgesteld» en publiceer naar het klantportaal om ze hier te zien."
              />
            )}
          </div>
        </section>

        {voorgesteldRejectedPairs.length > 0 ? (
          <section>
            <div className="mb-4 flex items-end justify-between gap-2 border-b border-slate-200 pb-3">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Extra: afgewezen kandidaten</h3>
                <p className="mt-1 text-sm text-slate-500">
                  Al afgewezen via het portaal of als afgewezen gemarkeerd in de pipeline — alleen archief, niet bij
                  openstaande dossiers geteld.
                </p>
              </div>
              <span className="shrink-0 rounded-full bg-slate-200 px-3 py-1 text-xs font-medium text-slate-700">
                {voorgesteldRejectedPairs.length}
              </span>
            </div>
            <div className="space-y-5">
              {voorgesteldRejectedPairs.map(({ app, proposal }) => (
                <VacancyClientPortalCandidateEditor
                  key={proposal.proposalId}
                  vacancy={vacancy}
                  application={app}
                  proposal={proposal}
                  user={user}
                  cvSnapshot={snapshotsByApplicationId[proposal.proposalId] ?? null}
                  onSaved={() => void load()}
                />
              ))}
            </div>
          </section>
        ) : null}

        {overigeActieList.length > 0 ? (
          <section>
            <div className="mb-4 border-b border-slate-200 pb-3">
              <h3 className="text-lg font-semibold text-slate-900">Overige openstaande dossiers</h3>
              <p className="mt-1 text-sm text-slate-500">Meer info, on hold of andere actie — zichtbaar voor de klant.</p>
            </div>
            <div className="space-y-5">
              {overigeActieList.map((p) => (
                <CandidateProposalCard
                  key={p.proposalId}
                  proposal={p}
                  noteText=""
                  readOnlyClientPortalUi
                  onAutosaveNote={noop}
                  onSendHeadsUp={noop}
                  onQuickAction={noop}
                  onCandidateProfileClick={() => openCandidate(p)}
                />
              ))}
            </div>
          </section>
        ) : null}

        <section>
          <div className="mb-4 border-b border-slate-200 pb-3">
            <h3 className="text-lg font-semibold text-slate-900">Agenda gesprekken</h3>
            <p className="mt-1 text-sm text-slate-500">Geplande gesprekken voor deze vacature (zelfde venster als klantportaal).</p>
          </div>
          {interviews.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-10 text-center text-sm text-slate-500">
              Geen geplande gesprekken in dit venster.
            </p>
          ) : (
            <ul className="space-y-3">
              {interviews.map((inv) => (
                <li
                  key={inv.id}
                  className="rounded-2xl border border-slate-200 bg-white p-4 text-sm shadow-sm ring-1 ring-slate-100/80"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="font-bold text-slate-900">{inv.candidateName}</p>
                      <p className="text-xs text-slate-500">{inv.vacancyTitle}</p>
                    </div>
                    <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-bold uppercase text-slate-600 ring-1 ring-slate-200">
                      {interviewStatusLabel(inv.status)}
                    </span>
                  </div>
                  <p className="mt-2 text-slate-700">
                    <span className="font-semibold">{formatNlDateTime(inv.startsAt)}</span>
                    <span className="mx-2 text-slate-300">·</span>
                    <span>{interviewTypeLabel(inv.type)}</span>
                  </p>
                  {inv.interviewers.length ? (
                    <p className="mt-1 text-xs text-slate-500">Met: {inv.interviewers.join(', ')}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <div className="mb-4 border-b border-slate-200 pb-3">
            <h3 className="text-lg font-semibold text-slate-900">Plaatsingen</h3>
            <p className="mt-1 text-sm text-slate-500">Plaatsingen gekoppeld aan deze vacature.</p>
          </div>
          {offers.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-10 text-center text-sm text-slate-500">
              Geen plaatsingen voor deze vacature.
            </p>
          ) : (
            <ul className="space-y-3">
              {offers.map((o) => (
                <li
                  key={o.id}
                  className="rounded-2xl border border-slate-200 bg-white p-4 text-sm shadow-sm ring-1 ring-slate-100/80"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="font-bold text-slate-900">{o.candidateName}</p>
                      <p className="text-xs text-slate-500">{o.vacancyTitle}</p>
                    </div>
                    <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-bold uppercase text-slate-600 ring-1 ring-slate-200">
                      {offerStatusLabel(o.status)}
                    </span>
                  </div>
                  <dl className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
                    <div>
                      <dt className="font-bold uppercase text-slate-400">Salaris</dt>
                      <dd className="font-semibold text-slate-800">{o.salaryRange}</dd>
                    </div>
                    <div>
                      <dt className="font-bold uppercase text-slate-400">Start</dt>
                      <dd className="font-semibold text-slate-800">{formatNlDate(o.startDate)}</dd>
                    </div>
                  </dl>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <EngagementCandidateModal
        open={candidateModal.open}
        proposal={candidateModal.proposal}
        cvSnapshot={modalSnapshot}
        onClose={closeCandidate}
      />
    </div>
  );
};

export default VacancyClientPortalTab;
